import styled from '@emotion/styled';
import { Box, Text } from '@mantine/core';

import { mediaQueryMobile, mediaQueryTablet } from '~/styles/mixins';

import UpraisedLogoComponent from '~/assets/upraised-logo.svg';
import CheckLogoComponent from '~/assets/icons/check.svg';
import ChevronDownLogoComponent from '~/assets/icons/chevron-down.svg';

const Flex = styled(Box)`
  display: flex;
`;

export const Root = styled(Box)`
  a {
    text-decoration: none;
  }
  height: 84px;
  ${mediaQueryTablet} {
    height: 72px;
  }
  ${mediaQueryMobile} {
    height: 56px;
  }
`;

export const HeaderRoot = styled(Box)`
  height: 84px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  border-bottom: 1px solid #e9e9e9;
  z-index: 1;
  background: #ffffff;

  ${mediaQueryTablet} {
    height: 72px;
  }
  ${mediaQueryMobile} {
    height: 56px;
  }
`;

export const Container = styled(Box)`
  max-width: 1272px; // 1272 = 1224 content + 24 padding on left and right
  margin: 0 auto;
  padding: 0 24px;
  height: 100%;

  ${mediaQueryTablet} {
    padding: 0 36px;
  }
  ${mediaQueryMobile} {
    padding: 0 20px;
  }
`;

export const LogoContainer = styled(Box)`
  cursor: pointer;
`;
LogoContainer.defaultProps = { component: 'a' };

export const UpraisedLogo = styled(UpraisedLogoComponent)`
  display: block;
  height: 32px;
  ${mediaQueryMobile} {
    height: 28px;
  }
`;

export const NavWrapper = styled(Flex)`
  align-items: center;
`;
NavWrapper.defaultProps = {
  component: 'nav',
};

export const DropdownRoot = styled(Box)`
  padding: 8px;
  position: absolute;
  background: #ffffff;
  min-width: 204px;
  box-shadow: 0px 4px 40px -8px rgba(56, 25, 0, 0.06), 0px 4px 40px rgba(56, 25, 0, 0.04);
  border-radius: 6px;
  z-index: 2;
  top: 32px;
  left: -16px;
  display: none;

  &:hover,
  &:focus {
    display: block;
  }
`;

export const NavContentWrapper = styled(Flex)`
  align-items: center;
  cursor: pointer;
`;

const NavLink = styled.a`
  text-decoration: none;
`;

export const NavItemText = styled(NavLink)`
  color: #555;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    transition: transform 0.3s ease;
    left: 0;
    bottom: -6px;
    width: 100%; //make it 50% for hover
    height: 3px;
    background: #ff7504;
    transform: scaleX(0);
  }

  &:hover:before {
    transform: scaleX(1);
  }
`;

export const ChevronDownLogo = styled(Box)`
  width: 24px;
  height: 24px;
  color: #555;
  flex-shrink: 0;
`;
ChevronDownLogo.defaultProps = { component: ChevronDownLogoComponent };

export const NavItem = styled(Box)`
  position: relative;
  background: #ffffff;
  border: none;
  /* to make sure dropdown remains open when cursor moves from NavItem to dropdown*/
  padding-bottom: 8px;
  margin-bottom: -8px;

  &:hover,
  &:focus {
    ${DropdownRoot} {
      display: block;
    }

    ${NavItemText} {
      color: #787878;

      &:before {
        transform: scaleX(1);
      }
    }
    ${ChevronDownLogo} {
      color: #787878;
    }
  }

  &:focus {
    ${NavItemText} {
      color: #3d3d3d;
    }
  }

  &:focus-within {
    ${DropdownRoot} {
      display: block;
    }
  }

  & + & {
    margin-left: 32px;
  }
`;

export const DropdownItemAnchor = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #fff;

  &:hover {
    background: #f5f5f5;
    border: 1px solid #f5f5f5;
  }

  &:focus {
    border: 1px dashed ${({ color }) => (color ? color : '#ff7504')};
    outline: none;
  }

  & + & {
    margin-top: 4px;
  }

  ${mediaQueryTablet} {
    max-width: 164px;

    & + & {
      margin-top: 0;
    }
  }
`;
DropdownItemAnchor.defaultProps = { component: 'a' };

export const DropdownItemContent = styled(Box)`
  margin-right: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DropdownItemText = styled(NavLink)`
  color: ${(props) => (props.isActive ? props.activeColor : '#3d3d3d')};

  ${mediaQueryTablet} {
    color: ${(props) => (props.isActive ? props.activeColor : '#3d3d3d')};
  }
`;
DropdownItemText.defaultProps = { component: 'span' };

export const DropdownItemSubText = styled(Text)`
  font-family: ${(p) => p.theme.fontFamily};
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  color: #787878;
  margin: 0;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CheckLogo = styled(Box)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: ${(props) => (props.color ? props.color : '#ff7504')};
`;
CheckLogo.defaultProps = { component: CheckLogoComponent };

export const HamburgerMenuIconLine = styled(Box)`
  background-color: #ff7504;
  border-radius: 6px;
  width: 100%;
  height: 3px;

  &.line-half-start {
    width: 50%;
    transition: transform 330ms cubic-bezier(0.54, -0.81, 0.57, 0.57);
    transform-origin: right;
  }
  &.line-half-end {
    width: 50%;
    align-self: flex-end;
    transition: transform 330ms cubic-bezier(0.54, -0.81, 0.57, 0.57);
    transform-origin: left;
  }
`;

export const HamburgerMenuIconContainer = styled(Flex)`
  width: 22px;
  height: 20px;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  transition: transform 330ms ease-out;

  &.navigation-menu-open {
    transform: rotate(-45deg);

    ${HamburgerMenuIconLine} {
      &.line-half-start {
        transform: rotate(-90deg) translateX(3px);
      }
      &.line-half-end {
        transform: rotate(-90deg) translateX(-3px);
      }
    }
  }
`;

export const ExtendedMenuContainer = styled(Box)`
  display: ${({ showExtendedMenu }) => (showExtendedMenu ? 'block' : 'none')};
  padding: 24px;
  background: #fff;
  position: absolute;
  box-shadow: 0px 4px 40px -8px rgba(56, 25, 0, 0.06), 0px 4px 40px rgba(56, 25, 0, 0.04);
  border-radius: 8px;
  width: 380px;
  right: -16px;

  ${mediaQueryMobile} {
    width: 100%;
    position: fixed;
    top: 54px;
    left: 0;
    right: 0;
    padding: 16px 12px 24px 12px;
    box-shadow: 0px 16px 16px -4px rgba(56, 25, 0, 0.06), 0px 16px 16px rgba(56, 25, 0, 0.04);
    border-radius: 0px 0px 8px 8px;
  }
`;

export const MenuContentWrapper = styled(Box)`
  max-width: 332px;
  margin: 0 auto;
`;

export const MenuNavItemsGroupHeading = styled(Text)`
  font-family: ${(p) => p.theme.other.fonts.body};
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #787878;
  margin: 0;
`;

export const MenuNavItemsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
  grid-template-rows: auto;
`;

export const PrimaryBtn = styled(Flex)`
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0 24px;
  height: 48px;
  border-radius: 48px;
  background-color: #ff7504;
  background: linear-gradient(90deg, #ff8e07 0%, #ff7504 50%, #ff7504 100%);
  box-shadow: none;
  font-family: ${(p) => p.theme.fontFamily};
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: -0.02em;
  color: #ffffff;
  border: none;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background-image: linear-gradient(90deg, #ffa030, #ff8c2d 50%, #ff8c2d);
    box-shadow: 0 0 16px 0 rgb(255 117 4 / 13%);
  }

  ${mediaQueryTablet} {
    height: 40px;
    padding: 0px 16px;
    font-size: 12px;
  }
`;
PrimaryBtn.defaultProps = {
  component: 'button',
};

export const OutlineBtn = styled(PrimaryBtn)`
  background: #fff;
  color: #ff7504;
  border: 1px solid #ff7504;
  box-shadow: none;
  text-decoration: none;

  &:hover {
    background: linear-gradient(90deg, #ff8e07 0%, #ff7504 50%, #ff7504 100%);
    box-shadow: 0px 0px 12px rgba(255, 117, 4, 0.08);
    color: #ffffff;
  }
`;
